import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { JsonView } from "@/components/json-view";
import { Panel } from "@/components/panel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AnalysisPanel } from "@/features/analysis";
import { apiGet, apiPost } from "@/lib/api-client";
import { asPercent } from "@/lib/format";
import { asRecord } from "@/lib/type-utils";

export function AnalysisDemoPage() {
  const [activeMode, setActiveMode] = useState<"quick" | "standard" | "deep">("quick");
  const [marketQuestion, setMarketQuestion] = useState("Will Bitcoin exceed $100K by end of 2025?");
  const [currentPrice, setCurrentPrice] = useState("0.50");
  const [analyzing, setAnalyzing] = useState(false);
  const [demoResult, setDemoResult] = useState<unknown>(null);
  const [demoError, setDemoError] = useState<string | null>(null);

  const modesQuery = useQuery({
    queryKey: ["analysis-demo", "modes"],
    queryFn: () => apiGet<unknown>("/api/workflow/modes"),
  });

  const runDemo = async () => {
    setAnalyzing(true);
    setDemoError(null);
    try {
      const parsedPrice = parseFloat(currentPrice);
      if (isNaN(parsedPrice) || parsedPrice < 0 || parsedPrice > 1) {
        throw new Error("Current price must be a valid number between 0 and 1");
      }
      // /api/workflow/analyze requires current_price and wraps its result as
      // {success, data} — unwrap before rendering.
      const resp = await apiPost<{ success: boolean; data: unknown }>(`/api/workflow/analyze`, {
        mode: activeMode,
        question: marketQuestion,
        current_price: parsedPrice,
      });
      setDemoResult(asRecord(resp).data);
    } catch (err) {
      setDemoResult(null);
      setDemoError(err instanceof Error ? err.message : "Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const modeDescriptions = {
    quick: {
      title: "Quick Analysis",
      description: "Fast AI scan with minimal research (30-60 seconds)",
      features: ["Single AI model", "Basic web search", "Price trend analysis", "Quick probability estimate"],
      color: "green",
    },
    standard: {
      title: "Standard Analysis",
      description: "Balanced research with multiple sources (2-3 minutes)",
      features: ["Multi-model consensus", "News aggregation", "Market data analysis", "Confidence interval"],
      color: "yellow",
    },
    deep: {
      title: "Deep Investigation",
      description: "Comprehensive research with adversarial debate (5-10 minutes)",
      features: ["AI council debate", "Deep research loop", "Resolution verification", "Full report"],
      color: "purple",
    },
  };

  const modeButtonClasses: Record<"quick" | "standard" | "deep", { active: string; title: string }> = {
    quick: { active: "border-green-500 bg-green-500/10", title: "text-green-400" },
    standard: { active: "border-yellow-500 bg-yellow-500/10", title: "text-yellow-400" },
    deep: { active: "border-purple-500 bg-purple-500/10", title: "text-purple-400" },
  };

  const currentMode = modeDescriptions[activeMode];

  return (
    <div className="space-y-4 md:space-y-6">
      <Panel title="Analysis Demo" description="Compare the three-tier market analysis pipeline.">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {( ["quick", "standard", "deep"] as const).map((mode) => {
            const desc = modeDescriptions[mode];
            const classes = modeButtonClasses[mode];
            return (
              <button
                key={mode}
                type="button"
                onClick={() => setActiveMode(mode)}
                className={`rounded-xl border-2 p-4 text-left transition-all ${
                  activeMode === mode
                    ? classes.active
                    : "border-gunmetal bg-gunmetal/30 hover:bg-gunmetal/50"
                }`}
              >
                <div className={`font-semibold ${activeMode === mode ? classes.title : ""}`}>
                  {desc.title}
                </div>
                <div className="mt-1 text-xs text-slate-400">{desc.description}</div>
              </button>
            );
          })}
        </div>
      </Panel>

      <Panel
        title={`${currentMode.title} Configuration`}
        description="Set parameters for the analysis demonstration"
        actions={
          <Button
            onClick={runDemo}
            disabled={analyzing}
          >
            {analyzing ? "Analyzing..." : "Run Analysis"}
          </Button>
        }
      >
        <div className="space-y-4">
          {demoError && (
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300" role="alert">
              Analysis failed: {demoError}
            </div>
          )}
          <div>
            <Label>Market Question</Label>
            <Textarea
              className="min-h-[80px] w-full"
              value={marketQuestion}
              onChange={(e) => setMarketQuestion(e.target.value)}
              placeholder="Enter a prediction market question..."
            />
          </div>

          <div>
            <Label>Current Market Price (0-1)</Label>
            <Input
              type="number"
              min="0"
              max="1"
              step="0.01"
              className="w-40"
              value={currentPrice}
              onChange={(e) => setCurrentPrice(e.target.value)}
              placeholder="0.50"
            />
          </div>

          <div className="rounded-lg bg-gunmetal/50 p-4">
            <div className="mb-3 font-medium">Analysis Features</div>
            <div className="space-y-2">
              {currentMode.features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2 text-sm">
                  <span className="text-green-400">✓</span>
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4 text-sm text-slate-400">
            <span>Expected duration: {activeMode === "quick" ? "30-60s" : activeMode === "standard" ? "2-3min" : "5-10min"}</span>
            <span>•</span>
            <span>API calls: {activeMode === "quick" ? "~5" : activeMode === "standard" ? "~15" : "~50+"}</span>
          </div>
        </div>
      </Panel>

      {demoResult != null && (
        <Panel title="Analysis Result" description={`${currentMode.title} output`}>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="rounded-lg bg-gunmetal/50 p-3 text-center">
                <div className="text-2xl font-bold text-gold-400">
                  {asRecord(demoResult).probability
                    ? asPercent(Number(asRecord(demoResult).probability))
                    : "--"}
                </div>
                <div className="text-xs text-slate-400">Probability</div>
              </div>
              <div className="rounded-lg bg-gunmetal/50 p-3 text-center">
                <div className="text-2xl font-bold text-gold-400">
                  {asRecord(demoResult).confidence
                    ? asPercent(Number(asRecord(demoResult).confidence))
                    : "--"}
                </div>
                <div className="text-xs text-slate-400">Confidence</div>
              </div>
              <div className="rounded-lg bg-gunmetal/50 p-3 text-center">
                <div className="text-2xl font-bold text-gold-400">
                  {/* Backend emits `direction` (BUY/SELL/HOLD); older keys kept as fallback. */}
                  {String(
                    asRecord(demoResult).direction ??
                      asRecord(demoResult).recommendation ??
                      asRecord(demoResult).action ??
                      "--",
                  )}
                </div>
                <div className="text-xs text-slate-400">Recommendation</div>
              </div>
            </div>

            {asRecord(demoResult).reasoning != null && (
              <div className="rounded-lg bg-gunmetal/50 p-4">
                <div className="mb-2 font-medium">Reasoning</div>
                <p className="text-sm text-slate-300">{String(asRecord(demoResult).reasoning)}</p>
              </div>
            )}

            <JsonView value={demoResult} />
          </div>
        </Panel>
      )}

      <Panel title="Analysis Modes Reference" description="Technical specifications for each tier">
        <div className="space-y-4">
          <div className="rounded-lg border-l-4 border-green-500 bg-gunmetal/50 p-4">
            <div className="mb-2 font-semibold text-green-400">Tier 1: Quick Analysis</div>
            <ul className="space-y-1 text-sm text-slate-400">
              <li>• Single GPT-4o query with basic web search</li>
              <li>• 1-2 source citations</li>
              <li>• ~$0.01-0.02 API cost</li>
              <li>• Best for: Rapid screening, well-known events</li>
            </ul>
          </div>

          <div className="rounded-lg border-l-4 border-yellow-500 bg-gunmetal/50 p-4">
            <div className="mb-2 font-semibold text-yellow-400">Tier 2: Standard Analysis</div>
            <ul className="space-y-1 text-sm text-slate-400">
              <li>• Multi-model consensus (GPT-4o, Claude, Gemini)</li>
              <li>• News aggregation from 5+ sources</li>
              <li>• ~$0.05-0.10 API cost</li>
              <li>• Best for: Most markets, medium complexity</li>
            </ul>
          </div>

          <div className="rounded-lg border-l-4 border-purple-500 bg-gunmetal/50 p-4">
            <div className="mb-2 font-semibold text-purple-400">Tier 3: Deep Investigation</div>
            <ul className="space-y-1 text-sm text-slate-400">
              <li>• 3-phase AI council debate with devil&apos;s advocate</li>
              <li>• Recursive research up to 5 iterations</li>
              <li>• Resolution verification and Brier score tracking</li>
              <li>• ~$0.20-0.50 API cost</li>
              <li>• Best for: High-value, controversial, or complex markets</li>
            </ul>
          </div>
        </div>
      </Panel>

      <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
        {/* Live multi-mode runner (scanner + council), mounted beside the
            workflow sandbox above — it hits the real endpoints, not the demo. */}
        <AnalysisPanel />
        <Panel title="Modes API" description="Raw analysis modes data">
          <JsonView value={modesQuery.data} />
        </Panel>
      </div>
    </div>
  );
}
